import { forwardRef, useCallback, useEffect } from 'react';
import { ui } from '@procuros/datachecks';

import { useErrorContext } from 'contexts/ErrorsNavigationContext/ErrorsNavigationContext';
import { WebEDIError } from 'services/webediErrors/types';
import { useWebEDIStore } from 'stores/webedi/webedi';
import { FieldState } from 'components/Form/types';
import { Translate } from 'components/Translate/Translate';
import { useEnumerableOptions } from 'components/WebEDI/helpers';

import { Dropdown } from '../Dropdown/Dropdown';

type MessageDropdownProps = Omit<
  Parameters<typeof Dropdown>[0],
  'errors' | 'fieldState' | 'options' | 'label' | 'multiple' | 'value' | 'onChange' | 'defaultValue'
> & {
  error: WebEDIError | undefined;
  totalNumberOfErrors: number;
  field: ui.EnumerableField;
  onShowFieldErrorModal?: (showFieldErrorModal: boolean) => void;
  value: string;
  onChange?: (value: string) => void;
  defaultValue?: string;
  fieldState: FieldState;
  variant?: 'large';
};

export const MessageDropdown = forwardRef<HTMLInputElement, MessageDropdownProps>(
  (
    {
      error,
      totalNumberOfErrors,
      onBlur,
      field,
      onShowFieldErrorModal,
      value,
      onChange,
      defaultValue,
      fieldState,
      variant,
      ...otherProps
    },
    ref,
  ) => {
    const { options } = useEnumerableOptions(field.type_config);
    const isValidating = useWebEDIStore((state) => state.isValidating);
    const { ErrorModalComponent, useErrorModal } = useErrorContext();
    const { showErrorModal, setShowErrorModal, floatingStyles, refs } = useErrorModal();
    const isLargeVariant = variant === 'large';

    const onDropdownFocus = useCallback(() => {
      setShowErrorModal(true);
    }, [setShowErrorModal]);

    const onDropdownBlur = useCallback(() => {
      onBlur?.();
      setShowErrorModal(false);
    }, [onBlur, setShowErrorModal]);

    useEffect(() => {
      if (error && showErrorModal) {
        onShowFieldErrorModal?.(true);
      }
    }, [error, showErrorModal, onShowFieldErrorModal]);

    const inlineErrors =
      isLargeVariant && error?.description ? { message: error.description, type: error.type ?? 'error' } : undefined;

    return (
      <div ref={isLargeVariant ? undefined : refs.setReference}>
        <Dropdown<string>
          {...otherProps}
          options={options}
          label={field.label}
          ref={ref}
          variant={variant}
          description={isLargeVariant && field.description ? <Translate i18nKey={field.description} /> : undefined}
          errors={inlineErrors}
          fieldState={fieldState}
          onFocus={isLargeVariant ? undefined : onDropdownFocus}
          onBlur={isLargeVariant ? onBlur : onDropdownBlur}
          dropdownDisplayTestId={'message-dropdown'}
          disabled={otherProps.disabled}
          multiple={false}
          value={value}
          onChange={onChange}
          defaultValue={defaultValue}
        />

        {!isLargeVariant && showErrorModal && ErrorModalComponent && (
          <ErrorModalComponent
            viewMode={false}
            error={error}
            ref={refs.setFloating}
            style={floatingStyles}
            numberOfErrors={totalNumberOfErrors}
            isValidating={isValidating}
            isPendingValidation={fieldState === 'pending-validation'}
            fieldDescription={field.description}
          />
        )}
      </div>
    );
  },
);

MessageDropdown.displayName = 'MessageDropdown';
